<template>
  <div class="flex h-full min-h-0 flex-col px-4 pb-2 pt-2 text-center">
    <!-- The body scrolls ONLY when the sheet was told its height and the content
         might not fit — a declared-height step, or a content-sized one whose
         content overflowed the tallest stop. When the sheet is sized to this
         content, nothing can overflow, and an `overflow-y-auto` there is a live
         scroll container for no reason: it absorbs the pan, so a sheet whose
         content plainly fits felt scrollable.
         `min-h-0` is what lets a flex child actually shrink to its container
         instead of forcing it taller. -->
    <div
      class="flex min-h-0 flex-1 flex-col items-center overscroll-contain"
      :class="canScroll ? 'overflow-y-auto' : 'overflow-hidden'"
    >
      <!-- Status icon -->
      <div
        class="mb-5 flex h-20 w-20 items-center justify-center rounded-full"
        :class="iconWrapClass"
      >
        <DsIcon :name="iconName" size="xl" :class="iconClass" />
      </div>

      <!-- Title -->
      <div class="mb-2 text-24-lh-24 font-black tracking-tight text-grey-800">{{ title }}</div>

      <!-- Body -->
      <p class="text-body-lg mb-6 font-medium text-grey-60">
        <template v-if="isSuccess">
          Paid
          <strong class="font-bold text-grey-800">{{ payAmount }} SGD</strong>{{ paidFor }}
        </template>
        <template v-else>{{ bodyMessage }}</template>
      </p>

      <!-- Receipt summary (success + pending) -->
      <div v-if="!isFailed" class="mb-6 flex w-full flex-col gap-3 rounded-2xl bg-brown-50 p-4">
        <div class="flex items-center justify-between gap-3">
          <span class="text-body-small-lg flex-none font-medium text-grey-60">Reference</span>
          <span class="text-body-small-lg break-all text-right font-bold text-grey-800">
            {{ referenceCode || transactionId || '—' }}
          </span>
        </div>
        <div class="h-px bg-brown-75" />
        <div class="flex items-center justify-between gap-3">
          <span class="text-body-small-lg flex-none font-medium text-grey-60">From</span>
          <span class="text-body-small-lg truncate text-right font-bold text-grey-800">
            {{ paidFrom || '—' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Actions, pinned to the sheet's visible foot. The scroll area above takes
         the slack (`flex-1`), so this sits at the bottom edge of a box that
         PaymentSheet MEASURES rather than predicts — the arithmetic version
         overshot by ~46pt and clipped this button off the screen. One action
         only: a failed payment was never charged, and sending the member back
         through the sheet to retry raced the ledger poll that may still resolve
         the original attempt. -->
    <div class="w-full flex-none pt-6">
      <DsButton class="w-full" variant="secondary" size="56" shape="pill" @click="$emit('done')">
        {{ isFailed ? 'Close' : 'Done' }}
      </DsButton>
    </div>
  </div>
</template>

<script>
import { Button as DsButton, Icon as DsIcon } from '@team-hogc/hogc-one-design-system';
import { GENERIC_FAILURE_MESSAGE, pendingMessage } from '@/constants/Payment/payment-methods';
import { triggerNotification } from '@/utils/haptics';

export default {
  name: 'PaymentSuccessStep',

  components: {
    DsButton,
    DsIcon,
  },

  props: {
    /**
     * Whether the body may scroll. False when the sheet has been sized to this
     * content, because then nothing can overflow and a scroll container would
     * only swallow the pan.
     */
    canScroll: {
      type: Boolean,
      default: false,
    },
    // 'success' | 'pending' | 'failed'
    status: {
      type: String,
      required: true,
      validator: value => ['success', 'pending', 'failed'].includes(value),
    },
    amount: {
      type: Number,
      default: 0,
    },
    description: {
      type: String,
      default: '',
    },
    transactionId: {
      type: String,
      default: null,
    },
    referenceCode: {
      type: String,
      default: null,
    },
    paidFrom: {
      type: String,
      default: null,
    },
    errorMessage: {
      type: String,
      default: null,
    },
  },

  emits: ['done'],

  computed: {
    isSuccess() {
      return this.status === 'success';
    },
    isPending() {
      return this.status === 'pending';
    },
    isFailed() {
      return this.status === 'failed';
    },
    payAmount() {
      return `$${Number(this.amount || 0).toLocaleString('en-SG', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    // One vocabulary for a payment's state across the whole app: these match
    // the Successful / Pending / Failed labels the My Payments list and the
    // receipt page already use, so the sheet is not the only surface with its
    // own words for the same thing.
    title() {
      if (this.isSuccess) return 'Payment successful';
      if (this.isPending) return 'Payment pending';
      return 'Payment failed';
    },

    // Assembled in JS, including its leading space. A template text node that
    // starts with whitespace is condensed away, which silently rendered
    // "$5 SGDfor the book" — interpolated values are left alone.
    paidFor() {
      return this.description ? ` for ${this.description}.` : '.';
    },
    bodyMessage() {
      // The store sets a rail-accurate message on every real outcome; these
      // only cover a step rendered without one.
      if (this.isPending) return this.errorMessage || pendingMessage();
      return this.errorMessage || GENERIC_FAILURE_MESSAGE;
    },
    iconName() {
      if (this.isSuccess) return 'check';
      if (this.isPending) return 'info';
      return 'close';
    },
    iconWrapClass() {
      if (this.isSuccess)
        return 'success-icon-container bg-scanner-success/15 text-scanner-success';
      if (this.isPending) return 'bg-orange-100 text-brown-700';
      return 'failed-icon-container bg-error text-white';
    },
    iconClass() {
      return this.isSuccess ? 'checkmark-icon' : '';
    },
  },

  mounted() {
    if (this.isFailed) {
      triggerNotification('error');
    } else if (this.isSuccess) {
      triggerNotification('success');
    } else {
      triggerNotification('warning');
    }
  },
};
</script>

<style scoped>
.success-icon-container {
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.checkmark-icon {
  opacity: 0;
  transform: scale(0.85);
  animation: drawCheck 0.5s ease-out 0.35s forwards;
}

@keyframes drawCheck {
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.failed-icon-container {
  animation:
    scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
    shake 0.5s ease-in-out 0.45s;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(4px);
  }
}
</style>
